import type { RouteLocationRaw, Router } from "vue-router";
import type { EMMIconName, IPopup } from "@mm-frontend/mm-web-ui-kit";
import type { EBottomBarItem, IBottomBarItem, IBottomBarItemNavigator, IBottomBarItemModal } from "../models/bottomBar.model";
import type { ERouteName } from "~/app/router/routeName.enum";

abstract class BottomBarItem {
  public readonly title: string;
  public readonly name: EBottomBarItem;
  public readonly icon: EMMIconName;
  public readonly router: Router;
  public readonly activeRoutes?: ERouteName[];

  constructor(
    { title, name, icon, router, activeRoutes }: IBottomBarItem
  ) {
    this.title = title;
    this.name = name;
    this.icon = icon;
    this.router = router;
    this.activeRoutes = activeRoutes;
  }

  public abstract navigate(): void;
  public abstract isActive(): boolean;
}

export class BottomBarItemNavigator extends BottomBarItem {
  public readonly navigateTo: RouteLocationRaw;

  constructor(
    { title, name, icon, router, navigateTo, activeRoutes }: IBottomBarItemNavigator
  ) {
    super({ title, name, icon, router, activeRoutes });
    this.navigateTo = navigateTo;
  }

  override navigate() {
    this.router.push(this.navigateTo);
  }

  override isActive(): boolean {
    return this.activeRoutes?.includes(this.router.currentRoute.value.name as ERouteName) ?? false;
  }
}

export class BottomBarItemModal extends BottomBarItem {
  public readonly popup: IPopup;

  constructor(
    { title, name, icon, router, popup, activeRoutes }: IBottomBarItemModal
  ) {
    super({ title, name, icon, router, activeRoutes });
    this.popup = popup;
  }

  override navigate() {
    const currentRoute = this.router.currentRoute;
    this.router.push({ ...currentRoute.value, hash: `#${this.name}` });
    
    if (this.isPopupOpened) {
      this.clearHash();
    }
  }

  public clearHash() {
    const currentRoute = this.router.currentRoute;
    this.router.push({ ...currentRoute.value, hash: '' });
  }

  public openPopup() {
    this.popup.show();
  }

  public closePopup() {
    if (this.isPopupOpened) {
      this.popup.close();
    }
  }

  override isActive(): boolean {
    const isActiveByRoute = this.activeRoutes?.includes(this.router.currentRoute.value.name as ERouteName) ?? false;
    const isActiveByHash = this.router.currentRoute.value.hash === `#${this.name}`;
    return isActiveByRoute || isActiveByHash;
  }

  get isPopupOpened() {
    return this.popup.isShown;
  }
}